import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from 'react';
import { useIntl } from 'umi';
import classNames from 'classnames';
import { Bubble } from 'tea-component';
import SvgIcon from '@home/components/SvgIcon';
import { EditInput } from '@/pages/datalab/components/TabsEdit';
import CommonBubble from '@home/components/CommonBubble';
import './index.scss';

interface IFilterNameEditProps {
  label: string;
  id: string;
  replaceList?: string[];
  onRenameTab: ({ id, label }: { id: string; label: string; }) => void;
  checkName?: (value: string, origin?: string) => boolean | Promise<boolean>;
  errorContent?: React.ReactNode;
  placement?: 'top' | 'bottom';
  overlayClassName?: string;
  onHidden?: () => void;
  type?: 'input' | 'textarea';
  className?: string;
}

const FilterNameEdit: React.FC<IFilterNameEditProps> = ({
  label,
  id,
  replaceList = ['And', 'Or', '且', '或'],
  onRenameTab,
  checkName,
  errorContent,
  placement = 'bottom',
  overlayClassName = '',
  onHidden,
  type = 'input',
  className,
}) => {
  const { formatMessage } = useIntl();
  const [show, setShow] = useState(false);
  const [isOverflow, setIsOverflow] = useState(false);
  const labelRef = useRef<HTMLSpanElement>(null);

  const labelContent = useMemo(() => {
    if (!label) return null;
    if (!replaceList?.length) return label;

    const escapeRegExp = (value: string): string =>
      value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const replacePattern = new RegExp(
      `(${replaceList.map(escapeRegExp).join('|')})`,
      'g',
    );
    const segments = label.split(replacePattern);

    return segments.map((segment, index) => {
      if (!segment) {
        return null;
      }
      const trimmed = segment.trim();
      if (trimmed && replaceList.includes(trimmed)) {
        return (
          <span
            key={`block-${index}`}
            className="dbl-filter-name-edit-block"
          >
            {trimmed}
          </span>
        );
      }

      return (
        <span key={`text-${index}`} className="dbl-filter-name-edit-text">
          {segment}
        </span>
      );
    });
  }, [label, replaceList]);

  const handleToggle = useCallback(
    (event?: React.MouseEvent) => {
      event?.stopPropagation();
      setShow(true);
    },
    []
  );

  const handleHidden = useCallback(() => {
    onHidden?.();
    setShow(false);
  }, [onHidden]);

  const checkOverflow = useCallback(() => {
    const element = labelRef.current;
    if (!element) {
      return;
    }
    const { scrollWidth, clientWidth, scrollHeight, clientHeight } = element;
    const hasHorizontalOverflow = scrollWidth - clientWidth > 1;
    const hasVerticalOverflow = scrollHeight - clientHeight > 1;
    setIsOverflow(hasHorizontalOverflow || hasVerticalOverflow);
  }, []);

  useEffect(() => {
    const element = labelRef.current;
    if (!element || typeof window === 'undefined') {
      return;
    }

    const triggerCheck = (): void => {
      window.requestAnimationFrame(() => {
        checkOverflow();
      });
    };

    triggerCheck();

    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(() => {
        triggerCheck();
      });
      resizeObserver.observe(element);

      return () => {
        resizeObserver.disconnect();
      };
    }

    window.addEventListener('resize', triggerCheck);
    return () => {
      window.removeEventListener('resize', triggerCheck);
    };
  }, [checkOverflow]);

  useEffect(() => {
    setTimeout(() => {
      checkOverflow();
    }, 0);
  }, [checkOverflow, label]);

  if (show) {
    return (
      <EditInput
        maxLength={2560}
        type={type}
        value={label}
        hasClear={true}
        onHidden={handleHidden}
        onChange={(value: string) => {
          onRenameTab({ id, label: value });
        }}
        checkValue={checkName}
        errorContent={
          errorContent ||
          formatMessage({ id: 'dbl_create_dashboard_tab_duplicate_name' })
        }
        placement={placement}
        overlayClassName={overlayClassName}
      />
    );
  }

  return (
    <CommonBubble
      content={isOverflow ? label : ''}
    >
      <div
        className={classNames('dbl-filter-name-edit', className)}
        onDoubleClick={handleToggle}
      >
        <span className="dbl-filter-name-edit-label" ref={labelRef}>
          {labelContent}
        </span>
        <Bubble
          content={formatMessage({ id: 'dbl_common_edit' })}
          dark
          placement="top"
        >
          <div className="dbl-filter-name-edit-icon" onClick={handleToggle}>
            <SvgIcon type="pencil-m" />
          </div>
        </Bubble>
      </div>
    </CommonBubble>
  );
};

export default FilterNameEdit;